import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Card, Col, Descriptions, Row, Space, Statistic, Tag } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import CreditsEditor from './CreditsEditor'
import {
  errorNotification,
  getRequest,
  trackingAccountUrl,
  trackingPricingUrl
} from '../../config'
import { accountProblems, errorSentence, formatAge, formatDateTime, modeDisplay } from './utils'

// Errors above warnings: the first sentence on the screen should be the one that stopped tracking
// working, not the one that will stop it working later.
const LEVEL_ORDER = { error: 0, warning: 1 }
const byLevel = (a, b) => (LEVEL_ORDER[a.level] || 9) - (LEVEL_ORDER[b.level] || 9)

// Where a problem is fixed. Everything but the price is a server setting explained on this page.
const PROBLEM_LINKS = { no_price: { to: '/tracking/pricing', label: 'Pricing' } }

// What the Telegram `misconfigured` and `low_credits` alerts open — often on a phone, at an awkward
// hour, by somebody who has only the alert's one line for context. So the page explains the
// problem, what it does to customers, and the fix; the server's own flags decide all three, because
// this dashboard cannot see the server's environment.
const AccountPage = () => {
  const [loading, setLoading] = useState(false)
  const [account, setAccount] = useState(null)
  // `undefined` until the request comes back: nothing here should claim "no price" while the
  // pricing call is still in flight.
  const [pricing, setPricing] = useState(undefined)

  const load = async () => {
    const response = await getRequest(trackingAccountUrl, 'ShipsGo account', setLoading)
    if (response.success) setAccount(response.data)
    else errorNotification(errorSentence(response, 'The ShipsGo account could not be loaded.'))
    // Best-effort: a failure here only means the no-price warning is missing from this page, and
    // the helper has already said so.
    const price = await getRequest(trackingPricingUrl('GH'), 'price', () => {})
    if (price.success) setPricing(price.data)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const mode = modeDisplay(account)
  const problems = [...accountProblems(account, pricing)].sort(byLevel)
  const credits = account ? account.creditsRemaining : null
  const creditsUnknown = credits === null || credits === undefined

  return (
    <PageLoader className="container" loading={loading}>
      <Card
        title="ShipsGo account"
        extra={
          <Button icon={<ReloadOutlined />} onClick={load}>
            Reload
          </Button>
        }
      >
        {problems.map(problem =>
          problem.key === 'misconfigured' ? (
            <Alert
              key={problem.key}
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Tracking is switched off on this server"
              description={
                <div>
                  <div>
                    {(account && account.misconfiguredReason) ||
                      'ShipsGo is not set up for production.'}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Customers see &apos;not available yet&apos;. Paid shipments cannot start: they
                    show as Needs attention (<code>start_failed</code>) and are re-tracked once it
                    is fixed. Fix the server setting and restart it; this banner clears by itself.
                  </div>
                </div>
              }
            />
          ) : (
            <Alert
              key={problem.key}
              type={problem.level}
              showIcon
              style={{ marginBottom: 12 }}
              message={problem.message}
              action={
                PROBLEM_LINKS[problem.key] ? (
                  <Link to={PROBLEM_LINKS[problem.key].to}>
                    {PROBLEM_LINKS[problem.key].label}
                  </Link>
                ) : null
              }
            />
          )
        )}

        {account ? (
          <div>
            <Space align="center" style={{ marginBottom: 16 }} wrap>
              <Tag color={mode.color} style={{ fontSize: 16, padding: '4px 12px' }}>
                {mode.label}
              </Tag>
              {mode.note ? <span style={{ opacity: 0.65 }}>{mode.note}</span> : null}
            </Space>

            <Descriptions bordered column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label="API token">
                {account.configured ? <Tag color="green">Set</Tag> : <Tag color="red">Missing</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Webhook secret">
                {account.webhookConfigured ? (
                  <Tag color="green">Set</Tag>
                ) : (
                  <Tag color="gold">Missing</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Last webhook received" span={2}>
                {account.lastWebhookAt
                  ? `${formatDateTime(account.lastWebhookAt)} (${formatAge(
                      account.lastWebhookAt
                    )} ago)`
                  : 'Never'}
              </Descriptions.Item>
            </Descriptions>

            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
              <Col xs={24} md={12}>
                <Statistic
                  title="Credits remaining"
                  value={creditsUnknown ? 'Unknown' : credits}
                  // The server's own flag, never a recomputation here: the threshold is its setting.
                  valueStyle={account.lowCredits === true ? { color: '#cf1322' } : undefined}
                />
                <div style={{ marginTop: 8, opacity: 0.65 }}>
                  {creditsUnknown ? (
                    <div>
                      ShipsGo only reports a balance when a shipment is created; none reported yet.
                    </div>
                  ) : null}
                  <div>
                    {`Last recorded ${formatDateTime(account.creditsSeenAt)} · alert below ${
                      account.lowCreditThreshold
                    }`}
                  </div>
                </div>
                <CreditsEditor onSaved={setAccount} />
              </Col>
              <Col xs={24} md={12}>
                <Statistic
                  title="Last sync"
                  value={
                    account.lastSyncAt
                      ? `${formatDateTime(account.lastSyncAt)} (${formatAge(
                          account.lastSyncAt
                        )} ago)`
                      : 'Never'
                  }
                />
              </Col>
              <Col xs={24} md={12}>
                {/* The carrier catalogue's own clock (spec: tracking-carrier-catalogue D4): the
                    nightly import of ShipsGo's carrier list, separate from the shipment sync. */}
                <Statistic
                  title="Carrier catalogue"
                  value={
                    account.catalogueSyncedAt
                      ? `${formatDateTime(account.catalogueSyncedAt)} (${formatAge(
                          account.catalogueSyncedAt
                        )} ago)`
                      : 'Never synced'
                  }
                />
                <div style={{ marginTop: 8, opacity: 0.65 }}>
                  {account.catalogueCarrierCount != null
                    ? `${account.catalogueCarrierCount} carriers listed by ShipsGo`
                    : 'Runs nightly at 03:20, or sync_shipsgo_carriers --apply by hand.'}
                </div>
                {account.catalogueLastError ? (
                  <div style={{ marginTop: 4, color: '#cf1322' }}>
                    Last run refused: {account.catalogueLastError}
                  </div>
                ) : null}
              </Col>
            </Row>
          </div>
        ) : null}
      </Card>
    </PageLoader>
  )
}

export default AccountPage
